/* eslint-disable react-hooks/exhaustive-deps */
// ─────────────────────────────────────────────────────────────────────────────
// ChecklistItemRow — quickReply variant
//
// All split types (EMAIL_SENT, WHATSAPP_SENT, LOG_PHONE_CALL, etc.) render as a
// single plain Button. On click it opens the relevant composer or TaskChecklistField
// dialog, and on send/completion calls postState(YES) to mark the item done.
//
// The dropdown (SplitActionButton arrow-menu with template shortcuts, phone-outcome
// items, and "Mark as sent without composing") has been removed. Skip / Mark Done /
// Reopen remain available via the kebab menu on each item's default-variant row in
// the checklist below the CTA card.
// ─────────────────────────────────────────────────────────────────────────────
import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    Button,
    Menu,
    MenuItem,
    Divider,
    Chip,
    Avatar,
    Switch,
    TextField,
    Select,
    FormControl,
    InputAdornment,
    Autocomplete,
    Collapse,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import assertApiOk from 'src/utils/assertApiOk';
import copyDebugId from 'src/utils/copyDebugId';
import { dispatch, useSelector } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { primary } from 'src/theme/palette';
import { CHECKLIST_ITEM_TYPE, CHECKLIST_COMPLETION_STATE, TASKS_V2_CHANGED_EVENT } from 'src/constants/tasksV2';
import { readChecklistItemValue, buildChecklistValuePayload } from 'src/utils/checklistItemValue';
// quickReplyButtonStyle removed — was imported but unused
import TaskChecklistField from 'src/pages/dashboard/tables/AllLeadTable/TaskChecklistField';
import IosEmailComposer from 'src/sections/tasks/composers/IosEmailComposer';
import IosWhatsAppComposer from 'src/sections/tasks/composers/IosWhatsAppComposerDialog';
import { parseWtmResolveResponse } from 'src/components/whatsappComponents/quickReplyUtils';
import useDeliveryStatus from 'src/sections/tasks/useDeliveryStatus';
import DeliveryBadge from 'src/sections/tasks/DeliveryBadge';
import { resolveLeadStatus, getStatusPrimaryLabel, PRIMARY_ACTION_STYLE, PRIMARY_BUTTON_SX, SPLIT_TRIGGER_SX } from 'src/sections/tasks/statusPrimaryAction';

const { YES, PAUSED, SKIPPED, PENDING } = CHECKLIST_COMPLETION_STATE;

// Backend auto-skips contact-dependent items when the lead has no email/phone on
// file (response_payload.auto_skipped) so an un-actionable item never blocks the
// checklist. Surface WHY rather than a bare "Skipped".
const AUTO_SKIP_LABEL = { no_email: 'No email', no_phone: 'No phone' };
const AUTO_SKIP_TOOLTIP = {
    no_email: 'Skipped automatically — this lead has no email address on file',
    no_phone: 'Skipped automatically — this lead has no phone number on file',
};

// Payment method options for PAYMENT_METHOD checklist items.
const PAYMENT_METHOD_OPTIONS = ['In full', 'GoCardless', 'Finance', 'PAYG'];

// Channels that support "Mark as sent manually".
const MARK_SENT_CHANNELS = new Set([
    CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
    CHECKLIST_ITEM_TYPE.EMAIL_SENT,
    CHECKLIST_ITEM_TYPE.SMS_SENT,
]);

// -----------------------------------------------
// assertCellReady — shared guard for endpoints that build URLs from
// taskId/itemId. Returns true when both are present and non-stub, false
// otherwise (and surfaces a toast + invokes onDone so the parent can refresh).
//
// Stale rows arise when an AUTO_APPLY template was never instantiated for the
// lead, or when the parent Task got archived between load + click. Without
// this guard the URL becomes /tasks/undefined/checklist/undefined/<endpoint>/
// which 404s silently and the next refresh wipes the entire row to PENDING.
// -----------------------------------------------
function assertCellReady(taskId, itemId, onDone) {
    if (!taskId || taskId === 'undefined' || !itemId || itemId === 'undefined') {
        dispatch(updateError({
            success: false,
            message: 'This checklist item is not ready yet — refreshing.',
        }));
        if (onDone) onDone();
        return false;
    }
    return true;
}

// -----------------------------------------------
// useMarkSentManually — POST /mark-sent-manually/ for a checklist item
// -----------------------------------------------
function useMarkSentManually({ practiceId, taskId, itemId, onDone, onMarkSentUndo }) {
    const [loading, setLoading] = useState(false);

    const postMarkSent = useCallback(async (note) => {
        if (!assertCellReady(taskId, itemId, onDone)) return;
        setLoading(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/mark-sent-manually/`,
                'POST',
                { note: note || '' }
            ), 'Mark-sent failed');
            if (onMarkSentUndo) {
                onMarkSentUndo({
                    message: 'Marked as sent manually',
                    undoFn: async () => {
                        await assertApiOk(callApi(
                            `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/undo-mark-sent/`,
                            'POST',
                            {}
                        ), 'Undo failed');
                        if (onDone) onDone();
                    },
                });
            }
            if (onDone) onDone();
        } catch {
            dispatch(updateError({ success: false, message: 'Could not mark as sent.' }));
        } finally {
            setLoading(false);
        }
    }, [practiceId, taskId, itemId, onDone, onMarkSentUndo]);

    return { loading, postMarkSent };
}

// -----------------------------------------------
// MarkSentConfirm — inline collapsible confirm panel with optional note field
// -----------------------------------------------
function MarkSentConfirm({ open, onConfirm, onCancel, loading, channel }) {
    const [note, setNote] = useState('');
    const channelLabel = {
        [CHECKLIST_ITEM_TYPE.WHATSAPP_SENT]: 'WhatsApp',
        [CHECKLIST_ITEM_TYPE.EMAIL_SENT]: 'email',
        [CHECKLIST_ITEM_TYPE.SMS_SENT]: 'SMS',
        [CHECKLIST_ITEM_TYPE.PHONE_CALLED]: 'call',
        [CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL]: 'call',
    }[channel] || 'message';

    const handleConfirm = () => {
        onConfirm(note);
        setNote('');
    };

    return (
        <Collapse in={open} unmountOnExit>
            <Box
                sx={{
                    mt: 0.75,
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.75 }}>
                    Mark {channelLabel} as sent without composing?
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Optional note…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    multiline
                    maxRows={3}
                    sx={{ mb: 0.75, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                />
                <Stack direction="row" spacing={0.75}>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={loading}
                        onClick={handleConfirm}
                        sx={{ height: 26, fontSize: '0.72rem', textTransform: 'none', px: 1.25 }}
                    >
                        {loading ? <CircularProgress size={12} /> : 'Confirm'}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        disabled={loading}
                        onClick={() => { setNote(''); onCancel(); }}
                        sx={{ height: 26, fontSize: '0.72rem', textTransform: 'none', px: 1.25 }}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Box>
        </Collapse>
    );
}

MarkSentConfirm.propTypes = {
    open: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    channel: PropTypes.string,
};
MarkSentConfirm.defaultProps = { loading: false, channel: '' };

// -----------------------------------------------
// CompletionAudit — small caption row shown once an item is done
// Shared by both the default and quickReply variants.
// -----------------------------------------------
function CompletionAudit({ item }) {
    const completedAt = item.completed_at || item.completedAt;
    if (!completedAt) return null;

    const actor = item.completed_by_practice_user || null;
    const fullName = actor?.full_name
        || item.completed_by_full_name
        || (typeof item.completed_by === 'string' ? item.completed_by : '')
        || (item.completed_by && typeof item.completed_by === 'object'
            ? `${item.completed_by.first_name || ''} ${item.completed_by.last_name || ''}`.trim() || item.completed_by.email
            : '');
    const avatarUrl = actor?.avatar_url || item.completed_by_avatar_url || undefined;
    const initial = (fullName || '?').charAt(0).toUpperCase();
    const isManual = item.completion_method === 'manual';
    const manualNote = item.completion_note || '';
    const dateStr = dayjs(completedAt).format('D MMM, HH:mm');

    const completionLine = isManual
        ? `Sent manually${fullName ? ` by ${fullName}` : ''} — ${dateStr}`
        : `Sent via CRM — ${dateStr}${fullName ? ` · ${fullName}` : ''}`;

    const inner = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
            {fullName && (
                <Avatar src={avatarUrl} sx={{ width: 14, height: 14, fontSize: '0.55rem', flexShrink: 0 }}>
                    {initial}
                </Avatar>
            )}
            <Typography variant="caption" sx={{ color: isManual ? 'text.secondary' : 'success.main', lineHeight: 1.3 }}>
                {completionLine}
            </Typography>
        </Box>
    );

    if (isManual && manualNote) {
        return (
            <Tooltip title={`Note: ${manualNote}`} placement="bottom-end">
                {inner}
            </Tooltip>
        );
    }
    return inner;
}

CompletionAudit.propTypes = { item: PropTypes.object.isRequired };

// Inline structured-value editors for the SYSTEM field item types.
//
// These used to PATCH `{ value }` onto the checklist item. There is no `value` field on the
// model or the serializer, so DRF discarded it and returned 200: the control updated, no
// error appeared, and the answer was gone on reload (the backend's own
// _assert_value_not_persisted test documents the drop). Staff lost payment methods, deposit
// amounts, clinicians and dates this way.
//
// They now write to the sanctioned home — response_payload on /complete/, one whitelisted
// key per type (see src/utils/checklistItemValue.js). Recording a value therefore also marks
// the step done, which is exactly what the equivalent table cells already do (YesNoToggleCell,
// PaymentMethodCell, …); use the row's status menu to reopen or skip.
function InlineValueEditor({ item, itemType, practiceId, taskId, onDone }) {
    const itemId = item.checklist_item_id || item.id;
    // Seed from where the value actually lives, so a saved answer is visible on reload
    // instead of an empty control.
    const [localValue, setLocalValue] = useState(() => readChecklistItemValue(itemType, item) ?? '');
    const [saving, setSaving] = useState(false);

    const practiceUsers = useSelector((state) => state.practiceUser.practiceUser.results || []);
    const savedValue = readChecklistItemValue(itemType, item);

    const patchValue = useCallback(async (val) => {
        if (saving) return;
        // Never record an EMPTY value — that would complete the step with a blank payload
        // (e.g. clearing a deposit amount, or clearing the staff picker, would mark the step
        // done with amount:'' / staff_user_id:''). Clearing is not a completion; use Reopen on
        // the row menu to undo a step. Guarded centrally so every editor branch inherits it.
        if (val === '' || val == null) return;
        if (!assertCellReady(taskId, itemId, onDone)) return;
        setSaving(true);
        try {
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'POST',
                {
                    completion_state: YES,
                    response_payload: buildChecklistValuePayload(itemType, val),
                }
            ), 'Save failed');
            // Table strips / widget counts listen for this — a value that completes a step
            // must move the same counters a table-cell completion does.
            if (typeof window !== 'undefined') window.dispatchEvent(new Event(TASKS_V2_CHANGED_EVENT));
            if (onDone) onDone();
        } catch {
            dispatch(updateError({ success: false, message: 'Could not save value' }));
        } finally {
            setSaving(false);
        }
    }, [practiceId, taskId, itemId, itemType, saving, onDone]);

    if (itemType === CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE) {
        const checked = localValue === 'yes' || localValue === true;
        const handleToggle = () => {
            const next = checked ? 'no' : 'yes';
            setLocalValue(next);
            patchValue(next);
        };
        return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.8rem', color: checked ? 'success.main' : 'text.disabled' }}>
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {checked ? 'Yes' : localValue === 'no' ? 'No' : 'Not set'}
                </Typography>
                <Switch
                    size="small"
                    checked={checked}
                    onChange={handleToggle}
                    disabled={saving}
                />
            </Stack>
        );
    }

    if (itemType === CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT) {
        return (
            <TextField
                size="small"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() => { if (String(localValue) !== String(savedValue ?? '')) patchValue(localValue); }}
                onKeyDown={(e) => { if (e.key === 'Enter') patchValue(localValue); }}
                placeholder="0.00 or N/A"
                disabled={saving}
                sx={{ mt: 0.5, width: 160 }}
                InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    sx: { fontSize: '0.82rem', height: 32 },
                }}
            />
        );
    }

    if (itemType === CHECKLIST_ITEM_TYPE.PAYMENT_METHOD) {
        return (
            <FormControl size="small" sx={{ mt: 0.5, minWidth: 160 }}>
                <Select
                    value={localValue || ''}
                    onChange={(e) => { setLocalValue(e.target.value); patchValue(e.target.value); }}
                    displayEmpty
                    disabled={saving}
                    sx={{ fontSize: '0.82rem', height: 32 }}
                >
                    <MenuItem value="" disabled><em>Select method…</em></MenuItem>
                    {PAYMENT_METHOD_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ fontSize: '0.82rem' }}>{opt}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    if (itemType === CHECKLIST_ITEM_TYPE.STAFF_PICKER) {
        const selectedUser = practiceUsers.find((u) => String(u.id) === String(localValue)) || null;
        return (
            <Autocomplete
                size="small"
                options={practiceUsers}
                value={selectedUser}
                onChange={(_, newVal) => {
                    const id = newVal ? String(newVal.id) : '';
                    setLocalValue(id);
                    patchValue(id);
                }}
                getOptionLabel={(u) => `${u.user?.first_name || ''} ${u.user?.last_name || ''}`.trim() || u.user?.email || String(u.id)}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                disabled={saving}
                sx={{ mt: 0.5, width: 220 }}
                renderInput={(params) => (
                    <TextField {...params} placeholder="Pick staff member…" sx={{ '& .MuiInputBase-root': { fontSize: '0.82rem', height: 32 } }} />
                )}
            />
        );
    }

    if (itemType === CHECKLIST_ITEM_TYPE.DATE_FIELD || itemType === CHECKLIST_ITEM_TYPE.SIMPLE_DATE) {
        const parsed = localValue ? dayjs(localValue) : null;
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    value={parsed}
                    onChange={(newDate) => {
                        const iso = newDate && newDate.isValid() ? newDate.toISOString() : '';
                        setLocalValue(iso);
                        if (iso) patchValue(iso);
                    }}
                    disabled={saving}
                    slotProps={{ textField: { size: 'small', sx: { mt: 0.5, width: 180, '& .MuiInputBase-root': { fontSize: '0.82rem', height: 32 } } } }}
                />
            </LocalizationProvider>
        );
    }

    return null;
}

InlineValueEditor.propTypes = {
    item: PropTypes.object.isRequired,
    itemType: PropTypes.string.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onDone: PropTypes.func,
};
InlineValueEditor.defaultProps = { onDone: null };

const STATE_ICON = {
    [YES]:    { icon: 'eva:checkmark-circle-2-fill', color: '#00AB55' },
    [PAUSED]: { icon: 'eva:pause-circle-outline',    color: '#FFC107' },
    [SKIPPED]:{ icon: 'eva:slash-outline',           color: '#919EAB' },
};

const COMPOSER_META = {
    [CHECKLIST_ITEM_TYPE.EMAIL_SENT]:          { label: 'Send Email',          resend: 'Re-send Email',          icon: 'eva:email-outline',            color: '#078DEE' },
    [CHECKLIST_ITEM_TYPE.WHATSAPP_SENT]:       { label: 'Send WhatsApp',       resend: 'Re-send WhatsApp',       icon: 'ri:whatsapp-fill',             color: '#25D366' },
    [CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL]:      { label: 'Log Phone Call',      resend: 'Log Another Call',       icon: 'eva:phone-outline',            color: '#7B61FF' },
    [CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST]: { label: 'Send Review Request', resend: 'Re-send Review Request', icon: 'eva:star-outline',             color: '#FF9800' },
    [CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM]:   { label: 'Send Consent Form',   resend: 'Re-send Consent Form',   icon: 'eva:file-text-outline',        color: '#00B8D9' },
    [CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN]: { label: 'Send Treatment Plan', resend: 'Re-send Treatment Plan', icon: 'eva:clipboard-outline',        color: '#36B37E' },
    [CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER]:   { label: 'Schedule reminder',   resend: 'Schedule another',       icon: 'eva:bell-outline',             color: '#7B61FF' },
    [CHECKLIST_ITEM_TYPE.ADD_NOTE]:            { label: 'Add note',            resend: 'Add another note',       icon: 'eva:file-text-outline',        color: '#00B8D9' },
    [CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK]:   { label: 'Send payment link',   resend: 'Re-send payment link',   icon: 'eva:credit-card-outline',      color: '#5BB5F5' },
    [CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT]:     { label: 'Upload document',     resend: 'Upload another',         icon: 'eva:upload-outline',           color: '#36B37E' },
};

// Types that render as a plain action button in the quickReply variant
const SPLIT_TYPES = new Set([
    CHECKLIST_ITEM_TYPE.EMAIL_SENT,
    CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
    CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL,
    CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST,
    CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM,
    CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN,
    // Non-comms split coverage (#174). Main button still delegates to the
    // legacy TaskChecklistField dialog flow; the arrow menu adds shortcuts.
    CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER,
    CHECKLIST_ITEM_TYPE.ADD_NOTE,
    CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK,
    CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT,
]);

// -----------------------------------------------
// useCompletionApi — POST completion_state to BE via /complete/ or /reopen/
// practiceId + taskId + itemId come from the item itself (checklist_item_id) or are passed in.
// -----------------------------------------------
function useCompletionApi({ practiceId, taskId, itemId, onDone }) {
    const [loading, setLoading] = useState(false);

    const postState = useCallback(async (completionState, responsePayload) => {
        if (!assertCellReady(taskId, itemId, onDone)) return;
        setLoading(true);
        try {
            const body = { completion_state: completionState };
            if (responsePayload) body.response_payload = responsePayload;
            const endpoint = completionState === PENDING ? 'reopen' : 'complete';
            await assertApiOk(callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/${endpoint}/`,
                'POST',
                completionState === PENDING ? {} : body
            ), 'State change failed');
            if (onDone) onDone();
            // Broadcast a page-wide signal so any header counter that needs to
            // refresh (e.g. lead-detail "Contact attempts" pill which walks
            // checklist items by type) can re-fetch without us threading a
            // callback through five layers of components. Lightweight pubsub
            // via window events — only listened to by surfaces that opt in.
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', {
                    detail: { taskId, itemId, completionState },
                }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to update checklist item.' }));
        } finally {
            setLoading(false);
        }
    }, [practiceId, taskId, itemId, onDone]);

    return { loading, postState };
}

// -----------------------------------------------
// CellDebugIcon — hover-reveal copy icon for checklist item rows
// -----------------------------------------------
function CellDebugIcon({ itemId, itemType, taskId, completionState }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        copyDebugId({ cell: itemId, item_type: itemType || '', task: taskId || '', state: completionState || '' }).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy cell debug ID'} placement="right">
            <Box
                component="span"
                className="cell-debug-icon"
                onClick={handleCopy}
                sx={{
                    cursor: 'pointer',
                    color: copied ? 'success.main' : 'text.disabled',
                    display: 'inline-flex', alignItems: 'center',
                    transition: 'opacity 0.15s, color 0.15s',
                    '&:hover': { color: 'primary.main' },
                }}
            >
                <Iconify icon="eva:copy-outline" width={12} />
            </Box>
        </Tooltip>
    );
}

CellDebugIcon.propTypes = {
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    itemType: PropTypes.string,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    completionState: PropTypes.string,
};

CellDebugIcon.defaultProps = {
    itemType: '',
    taskId: null,
    completionState: '',
};

// -----------------------------------------------
// ChecklistItemRow
//
// Props:
//   item         — checklist item object from task API
//   practiceId   — practice id (string or number)
//   taskId       — task id (string or number)
//   leadData     — task.lead object { id, first_name, last_name, lead_contact, phone }
//   onDone       — called after any state change (triggers parent refresh)
//   showDragHandle — whether to render a drag affordance (slideout uses it, detail view doesn't)
//   isDragOver   — highlight when dragged over
//   onDragStart/onDragOver/onDrop/onDragEnd — pass through for drag-and-drop
// -----------------------------------------------
export default function ChecklistItemRow({
    item,
    practiceId,
    taskId,
    leadData,
    onDone,
    onRefresh,
    onMarkSentUndo,
    showDragHandle,
    isDragOver,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    variant,
    onActionOverride,
}) {
    const [composerOpen, setComposerOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    // Anchor for the unified-primary split-button dropdown (secondary composer).
    const [splitMenuAnchor, setSplitMenuAnchor] = useState(null);
    const [markSentOpen, setMarkSentOpen] = useState(false);
    const [resolvedEmailTemplateId, setResolvedEmailTemplateId] = useState(null);
    // WTM — per-practice WhatsApp template resolved for a system-template
    // WHATSAPP_SENT item; { id, name } handed to the WA composer so it previews +
    // sends the mapped approved template (works even when the 24h window is closed).
    const [resolvedWaTemplate, setResolvedWaTemplate] = useState(null);

    // Detect WhatsApp connection: currentBusinessId === -1 means no WABA configured.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const whatsappNotConfigured = useSelector((state) => state?.whatsappV2?.whatsappNotConfigured === true);

    const itemType = item.item_type || CHECKLIST_ITEM_TYPE.CONFIRMATION;
    const isComposerType = itemType === CHECKLIST_ITEM_TYPE.EMAIL_SENT || itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT;

    // ETM resolve-on-open — when the email composer opens for a system-template
    // item, resolve the per-practice engagement template mapping and preload it.
    // `item.template_item` (UUID) is set by the BE for system-template checklist
    // items; null for manually-created items (→ no resolve, fall back to existing).
    // NOTE: declared AFTER itemType — the deps array below reads itemType at render
    // time, so this must sit below itemType's declaration (TDZ fix).
    const templateItemUuid = item.template_item || null;
    // Use the lead's own practice id for the resolve — not a caller-supplied prop.
    const resolvePracticeId = leadData?.practice || leadData?.practice_id || practiceId;
    useEffect(() => {
        if (!composerOpen || itemType !== CHECKLIST_ITEM_TYPE.EMAIL_SENT || !templateItemUuid || !resolvePracticeId) return undefined;
        let active = true;
        callApi(`/api/v1/practices/${resolvePracticeId}/checklist-email-mappings/resolve/?template_checklist_item=${templateItemUuid}`)
            .then((res) => {
                if (!active) return;
                const engTplId = res?.data?.[0]?.engagement_template_id;
                if (engTplId) setResolvedEmailTemplateId(String(engTplId));
            })
            .catch(() => { /* silent: fallback to action_config.template_id */ });
        return () => { active = false; };
    }, [composerOpen, itemType, templateItemUuid, resolvePracticeId]);
    // WTM resolve-on-open — mirror of the ETM effect for the WhatsApp composer.
    // When the WA composer opens for a system-template WHATSAPP_SENT item, resolve
    // the per-practice WhatsApp mapping and hand the matched template { id, name }
    // to the composer. ChatPage matches it (id-first) against the loaded template
    // list and sends via the approved-template path. Silent fallback (blank
    // composer) on miss/error.
    //
    // WHATSAPP_SENT ONLY: although WTM officially supports LEAD_NEXT_ACTION too,
    // this row renders IosWhatsAppComposer ONLY for WHATSAPP_SENT (see the
    // composer-dialogs block below — the {itemType === WHATSAPP_SENT} guard).
    // LEAD_NEXT_ACTION items render TaskChecklistField instead, which routes their
    // WhatsApp action through FollowUpPanel (Path B), so resolving them here would
    // be dead work. Path B does the LEAD_NEXT_ACTION WTM resolve.
    useEffect(() => {
        if (!composerOpen || itemType !== CHECKLIST_ITEM_TYPE.WHATSAPP_SENT || !templateItemUuid || !resolvePracticeId) return undefined;
        let active = true;
        callApi(`/api/v1/practices/${resolvePracticeId}/checklist-whatsapp-mappings/resolve/?template_checklist_item=${templateItemUuid}`)
            .then((res) => {
                if (!active) return;
                const wtm = parseWtmResolveResponse(res);
                if (wtm) setResolvedWaTemplate(wtm);
            })
            .catch(() => { /* silent: composer stays on its default (no prefill) */ });
        return () => { active = false; };
    }, [composerOpen, itemType, templateItemUuid, resolvePracticeId]);
    // Reset resolved templates when the composer closes so the next open always re-resolves.
    useEffect(() => {
        if (!composerOpen) {
            setResolvedEmailTemplateId(null);
            setResolvedWaTemplate(null);
        }
    }, [composerOpen]);
    // Effective template ID: resolved mapping wins, falls back to action_config.
    const effectiveEmailTemplateId = resolvedEmailTemplateId ?? item.action_config?.template_id ?? null;
    const isSplitType = SPLIT_TYPES.has(itemType);
    // Action types that have their own dialog/composer in TaskChecklistField — delegate
    // to it so the slideout fires the same UX as the inline lead-table cell.
    // Plain toggle types (CONFIRMATION without requires_confirmation, CHECKBOX, FREE_TEXT, etc.)
    // stay on the simple checkbox path below.
    const ACTION_TYPES_WITH_DIALOG = new Set([
        CHECKLIST_ITEM_TYPE.OPEN_FOLLOW_UP,
        CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT,
        CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE,
        CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND,
        CHECKLIST_ITEM_TYPE.START_TREATMENT,
        CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT,
        CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL,
        CHECKLIST_ITEM_TYPE.PHONE_CALLED,
        CHECKLIST_ITEM_TYPE.SMS_SENT,
        CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM,
        CHECKLIST_ITEM_TYPE.SEND_REVIEW_REQUEST,
        CHECKLIST_ITEM_TYPE.SEND_PAYMENT_LINK,
        CHECKLIST_ITEM_TYPE.SEND_TREATMENT_PLAN,
        CHECKLIST_ITEM_TYPE.ADD_NOTE,
        CHECKLIST_ITEM_TYPE.SCHEDULE_REMINDER,
        CHECKLIST_ITEM_TYPE.UPLOAD_DOCUMENT,
        CHECKLIST_ITEM_TYPE.LINKED_CHECKLIST,
        CHECKLIST_ITEM_TYPE.LEAD_STATUS_CHANGED,
        CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE,
        // LEAD_NEXT_ACTION resolves dynamically from lead status; TaskChecklistField
        // has the dedicated handler (line ~385) that renders the status-correct
        // action buttons. Without this entry the sidebar fell through to plain
        // checkbox and the generic LeadActionZone fallback opened the wrong popup
        // for non-NEW statuses (e.g. CONSULTATION_ATTENDED).
        CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION,
    ]);
    const hasActionDialog = ACTION_TYPES_WITH_DIALOG.has(itemType);
    // CONFIRMATION with requires_confirmation=true opens the Yes/No dialog —
    // delegate to TaskChecklistField. Without it, plain checkbox toggle.
    const requiresConfirmationDialog = itemType === CHECKLIST_ITEM_TYPE.CONFIRMATION
        && (item.action_config?.requires_confirmation === true);
    // Allow N/A — template author opts in per item. When true, the Skip menu
    // entry reads "Mark as N/A" and the SKIPPED-state chip shows "N/A".
    // Same SKIPPED completion-state code path either way — labeling only.
    const allowNa = item.action_config?.allow_na === true;
    // Allow Skip — template author opts in per item. NEW items default to false.
    // EXISTING items where allow_skip is undefined are treated as true to
    // preserve current behavior (no surprise UX regression on live templates).
    // If allow_na is true it overrides — runtime shows "Mark as N/A" regardless.
    const allowSkip = allowNa || item.action_config?.allow_skip !== false;

    const completionState = item.completion_state || (item.is_completed || item.completed ? YES : PENDING);
    const isDone = completionState === YES;
    const isInactive = completionState === PAUSED || completionState === SKIPPED;

    // Backend auto-skips contact-dependent items when the lead has no email/phone
    // on file (response_payload.auto_skipped = 'no_email' | 'no_phone'), so an
    // un-actionable item never blocks the checklist. Surface WHY it was skipped
    // instead of a bare "Skipped" the operator can't explain.
    const autoSkippedReason = item.response_payload?.auto_skipped;
    const autoSkipLabel = AUTO_SKIP_LABEL[autoSkippedReason] || null;
    const autoSkipTooltip = AUTO_SKIP_TOOLTIP[autoSkippedReason] || '';

    const itemId = item.checklist_item_id || item.id;

    const { loading, postState } = useCompletionApi({ practiceId, taskId: String(taskId), itemId: String(itemId), onDone });
    const { loading: markSentLoading, postMarkSent } = useMarkSentManually({ practiceId, taskId: String(taskId), itemId: String(itemId), onDone, onMarkSentUndo });

    const isMarkSentChannel = MARK_SENT_CHANNELS.has(itemType);

    const handleSimpleToggle = () => {
        if (loading) return;
        postState(isDone ? PENDING : YES);
    };

    const handleMenuSelect = (state) => {
        setMenuAnchor(null);
        postState(state);
    };

    const handleComposerSend = () => {
        setComposerOpen(false);
        // Mark as YES after successful send
        postState(YES);
    };

    const composerMeta = COMPOSER_META[itemType];
    const actionLabel = isDone ? composerMeta?.resend : composerMeta?.label;

    const phoneNumber = leadData?.lead_contact?.phone || leadData?.phone || '';
    const leadId = leadData?.id;

    const completedAt = item.completed_at || null;
    const deliveryStatus = useDeliveryStatus(
        practiceId,
        leadId,
        itemType,
        completionState,
        completedAt,
        null
    );

    // ── Render the left affordance ──
    const renderLeft = () => {
        if (isComposerType) {
            if (loading) {
                return (
                    <Box sx={{ p: '9px' }}>
                        <CircularProgress size={16} />
                    </Box>
                );
            }
            const stateConf = STATE_ICON[completionState];
            if (stateConf) {
                return (
                    <Tooltip title={actionLabel}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                            sx={{ p: '6px' }}
                        >
                            <Iconify icon={stateConf.icon} width={18} sx={{ color: stateConf.color }} />
                        </IconButton>
                    </Tooltip>
                );
            }
            // PENDING — show channel icon as send trigger
            return (
                <Tooltip title={composerMeta?.label || 'Send'}>
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setComposerOpen(true); }}
                        sx={{ p: '6px', color: composerMeta?.color || 'text.secondary', '&:hover': { bgcolor: alpha(composerMeta?.color || '#000', 0.08) } }}
                    >
                        <Iconify icon={composerMeta?.icon || 'eva:email-outline'} width={18} />
                    </IconButton>
                </Tooltip>
            );
        }

        // Action-type cells (BOOK_APPOINTMENT, OPEN_FOLLOW_UP, etc.) — delegate
        // to the inline TaskChecklistField so the slideout fires the same dialogs
        // (smile-sim, attendance, treatment-started, follow-up popup, etc.) as
        // the lead-table cell.
        if (hasActionDialog || requiresConfirmationDialog) {
            const cellPayload = {
                ...item,
                task_id: taskId || item.task_id,
                checklist_item_id: itemId,
            };
            const colMetaShim = {
                id: itemId,
                item_type: itemType,
                action_config: item.action_config || {},
                instantiation_mode: 'TRIGGER_ONLY',
                is_role_allowed: item.is_role_allowed !== false,
            };
            return (
                <Box sx={{ minWidth: 0, '& > *': { maxWidth: '100%' } }}>
                    <TaskChecklistField
                        cellData={cellPayload}
                        colMeta={colMetaShim}
                        practiceId={practiceId}
                        leadData={leadData}
                        onUpdate={onDone}
                        onRefresh={onRefresh}
                    />
                </Box>
            );
        }

        // Plain type — checkbox (CONFIRMATION without requires_confirmation, CHECKBOX, FREE_TEXT, etc.)
        if (loading) {
            return (
                <Box sx={{ p: '9px' }}>
                    <CircularProgress size={16} />
                </Box>
            );
        }
        return (
            <Checkbox
                size="small"
                checked={isDone}
                onChange={handleSimpleToggle}
                sx={{
                    p: '6px',
                    color: isDone ? '#00AB55' : 'text.disabled',
                    '&.Mui-checked': { color: '#00AB55' },
                }}
            />
        );
    };


    // No-connection variant: WhatsApp not connected to CRM — show "Mark as sent" only.
    if (variant === 'quickReply' && itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && whatsappNotConfigured) {
        return (
            <>
                <Tooltip title="WhatsApp not connected to CRM — confirm the message was sent externally.">
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => setMarkSentOpen((p) => !p)}
                            disabled={markSentLoading}
                            startIcon={<Iconify icon="eva:checkmark-circle-outline" width={14} />}
                            sx={{ height: 30, fontSize: '0.75rem', textTransform: 'none' }}
                        >
                            Mark as sent
                        </Button>
                    </span>
                </Tooltip>
                <MarkSentConfirm
                    open={markSentOpen}
                    onConfirm={(note) => { setMarkSentOpen(false); postMarkSent(note); }}
                    onCancel={() => setMarkSentOpen(false)}
                    loading={markSentLoading}
                    channel={itemType}
                />
            </>
        );
    }

    if (variant === 'quickReply' && isSplitType) {
        // Unified primary "Follow Up" (#unified-primary): ONE consistent style
        // for the primary button across every item type and status — colour +
        // icon come from the shared PRIMARY_ACTION_STYLE, never from
        // COMPOSER_META. The per-action composer (Send Email / WhatsApp / …,
        // with its own colour/icon) is demoted to the split-button DROPDOWN.
        const primaryStatus = resolveLeadStatus(leadData);
        const primaryLabel = getStatusPrimaryLabel(primaryStatus);
        // The unified primary opens the per-status popup via the host's Follow Up
        // opener (handleActionZoneSuccess('FOLLOW_UP') in TaskBodyView). We only
        // show it when (a) the status has a forward action (label != null —
        // CLOSED returns null → no primary) AND (b) the host wired the opener.
        const showUnifiedPrimary = !!primaryLabel && !!onActionOverride;
        // Fallback colour for the legacy composer-primary (when no unified
        // primary is shown, e.g. CLOSED or opener not wired).
        const actionColor = composerMeta?.color || primary.main;
        const openComposer = () => setComposerOpen(true);

        return (
            <>
                {showUnifiedPrimary ? (
                    // Split button: uniform "Follow Up" primary + dropdown for the composer.
                    <Box sx={{ display: 'inline-flex', alignItems: 'stretch' }}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={loading}
                            onClick={() => onActionOverride()}
                            startIcon={loading
                                ? <CircularProgress size={12} sx={{ color: 'inherit' }} />
                                : <Iconify icon={PRIMARY_ACTION_STYLE.icon} width={14} />
                            }
                            sx={PRIMARY_BUTTON_SX}
                        >
                            {primaryLabel}
                        </Button>
                        <Tooltip title="More actions" placement="top">
                            <Button
                                size="small"
                                variant="contained"
                                aria-label="More actions"
                                onClick={(e) => setSplitMenuAnchor(e.currentTarget)}
                                sx={SPLIT_TRIGGER_SX}
                            >
                                <Iconify icon="eva:arrow-ios-downward-fill" width={16} />
                            </Button>
                        </Tooltip>
                        <Menu
                            anchorEl={splitMenuAnchor}
                            open={Boolean(splitMenuAnchor)}
                            onClose={() => setSplitMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem
                                dense
                                onClick={() => {
                                    setSplitMenuAnchor(null);
                                    // Log Phone Call's composer is the FollowUp popup itself;
                                    // for the rest open their dedicated composer dialog.
                                    if (onActionOverride && itemType === CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL) { onActionOverride(); return; }
                                    openComposer();
                                }}
                                sx={{ gap: 1 }}
                            >
                                <Iconify icon={composerMeta?.icon || 'eva:email-outline'} width={16} sx={{ color: actionColor }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                    {isDone ? (composerMeta?.resend || composerMeta?.label) : composerMeta?.label}
                                </Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                ) : (
                    // No unified primary (CLOSED status or opener not wired) — keep the
                    // legacy composer button as the primary so the action stays reachable.
                    <Button
                        size="small"
                        variant="contained"
                        disabled={loading}
                        onClick={() => {
                            if (onActionOverride && itemType === CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL) { onActionOverride(); return; }
                            setComposerOpen(true);
                        }}
                        startIcon={loading
                            ? <CircularProgress size={12} sx={{ color: 'inherit' }} />
                            : <Iconify icon={composerMeta?.icon || 'eva:email-outline'} width={14} />
                        }
                        sx={{
                            height: 32,
                            fontSize: '0.78rem',
                            textTransform: 'none',
                            bgcolor: actionColor,
                            '&:hover': { bgcolor: actionColor, filter: 'brightness(0.9)' },
                            px: 1.5,
                            borderRadius: 1,
                        }}
                    >
                        {isDone ? (composerMeta?.resend || composerMeta?.label) : composerMeta?.label}
                    </Button>
                )}
                {isDone && isComposerType && (
                    <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <DeliveryBadge status={deliveryStatus} inline />
                    </Box>
                )}
                {isDone && isComposerType && <CompletionAudit item={item} />}
                {/* Composer dialogs */}
                {composerOpen && itemType === CHECKLIST_ITEM_TYPE.EMAIL_SENT && (
                    <IosEmailComposer
                        open={composerOpen}
                        onClose={() => setComposerOpen(false)}
                        practiceId={practiceId}
                        leadId={leadData?.id}
                        leadDetail={leadData}
                        templateId={effectiveEmailTemplateId}
                        onSendSuccess={handleComposerSend}
                    />
                )}
                {composerOpen && itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && (
                    <IosWhatsAppComposer
                        open={composerOpen}
                        onClose={() => setComposerOpen(false)}
                        practiceId={practiceId}
                        leadId={leadData?.id}
                        phoneNumber={leadData?.lead_contact?.phone || leadData?.phone || ''}
                        leadDetail={leadData}
                        prefillWaTemplateId={resolvedWaTemplate?.id ?? null}
                        prefillWaTemplateName={resolvedWaTemplate?.name ?? null}
                        prefillWaItemId={itemId}
                        onSendSuccess={handleComposerSend}
                    />
                )}
                {/* Non-composer split types: render TaskChecklistField inline; its own click fires the dialog. */}
                {composerOpen && !isComposerType && (() => {
                    const cellPayload = { ...item, task_id: taskId || item.task_id, checklist_item_id: itemId };
                    const colMetaShim = { id: itemId, item_type: itemType, action_config: item.action_config || {}, instantiation_mode: 'TRIGGER_ONLY', is_role_allowed: item.is_role_allowed !== false };
                    return (
                        <Box sx={{ mt: 0.5 }}>
                            <TaskChecklistField
                                cellData={cellPayload}
                                colMeta={colMetaShim}
                                practiceId={practiceId}
                                leadData={leadData}
                                onUpdate={() => { setComposerOpen(false); if (onDone) onDone(); }}
                                onRefresh={onRefresh}
                            />
                        </Box>
                    );
                })()}
            </>
        );
    }

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                draggable={showDragHandle}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
                sx={{
                    py: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isDragOver ? 'primary.main' : 'transparent',
                    bgcolor: isDragOver ? alpha(primary.main, 0.04) : 'transparent',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '& .cell-debug-icon': { opacity: 0 },
                    '&:hover': {
                        bgcolor: 'action.hover',
                        '& .drag-handle': { opacity: 1 },
                        '& .cell-debug-icon': { opacity: 1 },
                    },
                }}
            >
                {showDragHandle && (
                    <Box
                        className="drag-handle"
                        sx={{ opacity: 0.45, cursor: 'grab', color: 'text.disabled', flexShrink: 0, display: 'flex', alignItems: 'center', transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}
                    >
                        <Iconify icon="eva:menu-2-fill" width={14} />
                    </Box>
                )}

                {renderLeft()}

                <Box sx={{ flex: 1, minWidth: 0, pt: '2px' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                        <Typography
                            variant="body2"
                            sx={{
                                textDecoration: isDone || completionState === SKIPPED ? 'line-through' : 'none',
                                color: isDone || isInactive ? 'text.disabled' : 'text.primary',
                                lineHeight: 1.5,
                                fontSize: '0.875rem',
                            }}
                        >
                            {item.title}
                        </Typography>
                        {item.is_required && !isDone && (
                            <Chip
                                label="Required"
                                size="small"
                                sx={{
                                    height: 18, fontSize: '0.62rem', fontWeight: 700,
                                    bgcolor: 'rgba(255,72,66,0.08)', color: '#FF4842',
                                    border: '1px solid rgba(255,72,66,0.24)',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}
                        {completionState === SKIPPED && (
                            <Tooltip title={autoSkipTooltip} arrow disableHoverListener={!autoSkipLabel}>
                                <Chip
                                    // Auto-skipped (no contact detail on file) → name the
                                    // reason ("No email" / "No phone"). Otherwise label varies
                                    // by template opt-in: "N/A" when action_config.allow_na is
                                    // true (semantic = "this didn't apply"), "Skipped" otherwise
                                    // (semantic = "operator chose to skip").
                                    label={autoSkipLabel || (allowNa ? 'N/A' : 'Skipped')}
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.625rem',
                                        fontWeight: 700,
                                        bgcolor: 'grey.200',
                                        // Align chip color with the strikethrough title's text.disabled so
                                        // the N/A label doesn't read more prominently than the title it
                                        // attaches to. Matches the slash-icon affordance tone.
                                        color: 'text.disabled',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Tooltip>
                        )}
                        {completionState === PAUSED && (
                            <Chip label="Paused" size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(255,193,7,0.12)', color: '#B78103', '& .MuiChip-label': { px: 0.75 } }} />
                        )}
                        {isComposerType && isDone && (
                            <DeliveryBadge status={deliveryStatus} inline />
                        )}
                        {/* Hover-reveal cell debug copy icon */}
                        <CellDebugIcon
                            itemId={itemId}
                            itemType={itemType}
                            taskId={item.task_id || taskId}
                            completionState={completionState}
                        />
                        {/* Universal kebab — opens the shared state Menu (Mark Done / Reopen / Skip)
                            so every action type can be Skipped/N/A regardless of cell affordance.
                            Hidden when the composer's left state-icon already opens the same menu
                            (YES / PAUSED / SKIPPED) to avoid two trigger surfaces for the same anchor. */}
                        {!(isComposerType && STATE_ICON[completionState]) && (
                            <Tooltip title="More options" placement="top">
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                                    sx={{
                                        p: '2px',
                                        ml: 'auto',
                                        color: 'text.disabled',
                                        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                                    }}
                                    aria-label="checklist item options"
                                >
                                    <Iconify icon="eva:more-vertical-fill" width={14} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>

                    {/* Completion audit — read-only, shown when item is done */}
                    {isDone && <CompletionAudit item={item} />}

                    {/* Inline structured-value editors for new SYSTEM template types */}
                    {(itemType === CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE
                        || itemType === CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT
                        || itemType === CHECKLIST_ITEM_TYPE.PAYMENT_METHOD
                        || itemType === CHECKLIST_ITEM_TYPE.STAFF_PICKER
                        || itemType === CHECKLIST_ITEM_TYPE.DATE_FIELD
                        || itemType === CHECKLIST_ITEM_TYPE.SIMPLE_DATE) && (
                        <InlineValueEditor
                            item={item}
                            itemType={itemType}
                            practiceId={practiceId}
                            taskId={taskId}
                            onDone={onDone}
                        />
                    )}

                    {/* Send button(s) for composer items in PENDING state */}
                    {isComposerType && completionState === PENDING && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                            <Button
                                size="small"
                                variant="contained"
                                onClick={(e) => { e.stopPropagation(); setComposerOpen(true); }}
                                sx={{
                                    height: 24, fontSize: '0.72rem', textTransform: 'none',
                                    bgcolor: composerMeta?.color, '&:hover': { bgcolor: composerMeta?.color, filter: 'brightness(0.9)' },
                                    px: 1.25, borderRadius: 1,
                                }}
                                startIcon={<Iconify icon={composerMeta?.icon} width={12} />}
                            >
                                {composerMeta?.label}
                            </Button>
                            {isMarkSentChannel && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={(e) => { e.stopPropagation(); setMarkSentOpen((p) => !p); }}
                                    sx={{ height: 24, fontSize: '0.72rem', textTransform: 'none', px: 1.25, borderRadius: 1 }}
                                >
                                    Mark as sent
                                </Button>
                            )}
                        </Stack>
                    )}
                    {/* Mark-as-sent confirm panel (default variant) */}
                    {isMarkSentChannel && (
                        <MarkSentConfirm
                            open={markSentOpen}
                            onConfirm={(note) => { setMarkSentOpen(false); postMarkSent(note); }}
                            onCancel={() => setMarkSentOpen(false)}
                            loading={markSentLoading}
                            channel={itemType}
                        />
                    )}
                </Box>

                {item.template_item_id && (
                    <Tooltip title="From template">
                        <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0 }}>
                            <Iconify icon="eva:bookmark-outline" width={14} />
                        </Box>
                    </Tooltip>
                )}
            </Stack>

            {/* Status context menu — universal: every action type can be Marked Done, Reopened, or Skipped (N/A).
                For composer items the first row also exposes the Send/Re-send shortcut. */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{ sx: { minWidth: 160, borderRadius: 1.5 } }}
            >
                {composerMeta && (
                    <MenuItem dense onClick={() => { setMenuAnchor(null); setComposerOpen(true); }} sx={{ gap: 1 }}>
                        <Iconify icon={composerMeta?.icon || 'eva:email-outline'} width={16} sx={{ color: composerMeta?.color }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{actionLabel}</Typography>
                    </MenuItem>
                )}
                {composerMeta && <Divider sx={{ my: 0.5 }} />}
                {completionState !== YES && (
                    <MenuItem dense onClick={() => handleMenuSelect(YES)} sx={{ gap: 1 }}>
                        <Iconify icon="eva:checkmark-circle-2-fill" width={16} sx={{ color: 'success.main' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Mark Done</Typography>
                    </MenuItem>
                )}
                {completionState !== PENDING && (
                    <MenuItem dense onClick={() => handleMenuSelect(PENDING)} sx={{ gap: 1 }}>
                        <Iconify icon="eva:undo-outline" width={16} sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Reopen</Typography>
                    </MenuItem>
                )}
                {completionState !== SKIPPED && allowSkip && (
                    <MenuItem dense onClick={() => handleMenuSelect(SKIPPED)} sx={{ gap: 1 }}>
                        <Iconify icon="eva:slash-outline" width={16} sx={{ color: 'text.disabled' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{allowNa ? 'Mark as N/A' : 'Skip'}</Typography>
                    </MenuItem>
                )}
            </Menu>

            {/* Email composer */}
            {composerOpen && itemType === CHECKLIST_ITEM_TYPE.EMAIL_SENT && (
                <IosEmailComposer
                    open={composerOpen}
                    onClose={() => setComposerOpen(false)}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadData}
                    templateId={effectiveEmailTemplateId}
                    onSendSuccess={handleComposerSend}
                />
            )}

            {/* WhatsApp composer (default render block) — must carry the SAME
                mapped-template prefill props as the quickReply/split block above,
                or a WTM-resolved template opens a blank composer on this path. */}
            {composerOpen && itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && (
                <IosWhatsAppComposer
                    open={composerOpen}
                    onClose={() => setComposerOpen(false)}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadData}
                    phoneNumber={phoneNumber}
                    prefillWaTemplateId={resolvedWaTemplate?.id ?? null}
                    prefillWaTemplateName={resolvedWaTemplate?.name ?? null}
                    prefillWaItemId={itemId}
                    onSendSuccess={handleComposerSend}
                />
            )}
        </>
    );
}

ChecklistItemRow.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        checklist_item_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        is_completed: PropTypes.bool,
        completed: PropTypes.bool,
        completion_state: PropTypes.string,
        is_required: PropTypes.bool,
        item_type: PropTypes.string,
        action_config: PropTypes.object,
        template_item_id: PropTypes.any,
        template_item: PropTypes.string, // UUID of TaskTemplateChecklistItem; null for non-system items
        completion_method: PropTypes.string,
        completion_note: PropTypes.string,
    }).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadData: PropTypes.object,
    onDone: PropTypes.func,
    onRefresh: PropTypes.func,
    onMarkSentUndo: PropTypes.func,
    showDragHandle: PropTypes.bool,
    isDragOver: PropTypes.bool,
    onDragStart: PropTypes.func,
    onDragOver: PropTypes.func,
    onDrop: PropTypes.func,
    onDragEnd: PropTypes.func,
    variant: PropTypes.oneOf(['default', 'quickReply']),
    onActionOverride: PropTypes.func,
};

ChecklistItemRow.defaultProps = {
    leadData: null,
    onDone: null,
    onRefresh: null,
    onMarkSentUndo: null,
    showDragHandle: false,
    isDragOver: false,
    onDragStart: undefined,
    onDragOver: undefined,
    onDrop: undefined,
    onDragEnd: undefined,
    variant: 'default',
};
